import React, { useState, useEffect, useMemo } from 'react';
import Swal from 'sweetalert2';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { 
  FaEye, 
  FaTrashAlt, 
  FaTimes, 
  FaSearch, 
  FaChevronDown, 
  FaChevronUp, 
  FaFileExport,
  FaBuilding,
  FaUserTie,
  FaEnvelope,
  FaPhoneAlt,
  FaCalendarAlt,
  FaCommentDots
} from 'react-icons/fa';
import { BsCheck2All } from 'react-icons/bs';
import { Helmet } from "react-helmet-async";
import './ViewContact.css';
import axiosInstance from '../../api/axiosInstance';
import Pagination from '../Pagination/Pagination';

/* =========================================================
   ULTRA-MODERN POPUP MODAL COMPONENT
========================================================= */
const ContactViewModal = ({ contact, onClose, onResolve, onDelete }) => {
    if (!contact) return null;
    const formatDate = (d) => d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : 'N/A';
    
    return (
        <div className="view-contact-modal-overlay" onClick={onClose}>
            <div className="view-contact-modal-content" onClick={(e) => e.stopPropagation()}>
                
                {/* Modal Header */}
                <div className="view-contact-modal-header">
                    <div className="view-contact-modal-header-left">
                        <span className="view-contact-modal-kicker">Submission Details</span>
                        <h2>{contact.name}</h2>
                    </div>
                    <div className="view-contact-modal-header-right">
                        <span className={`view-contact-status-badge ${contact.status.toLowerCase().replace(' ', '-')}`}>
                            {contact.status}
                        </span>
                        <button className="view-contact-modal-close-btn" onClick={onClose} aria-label="Close Details">
                            <FaTimes />
                        </button>
                    </div>
                </div>

                {/* Modal Body */}
                <div className="view-contact-modal-body">
                    
                    {/* Top Service Highlight Banner */}
                    <div className="view-contact-modal-service-banner">
                        <span className="view-contact-service-banner-label">Service Required:</span>
                        <strong>{contact.serviceRequired || 'General Institutional Advisory'}</strong>
                    </div>

                    {/* Metadata 2-Column Grid */}
                    <div className="view-contact-modal-grid">
                        
                        {/* Organisation */}
                        <div className="view-contact-modal-card">
                            <div className="view-contact-modal-icon-wrap">
                                <FaBuilding />
                            </div>
                            <div className="view-contact-modal-info">
                                <label>Organisation / Trust / Hospital</label>
                                <span>{contact.organisation || 'Not Specified'}</span>
                            </div>
                        </div>

                        {/* Designation */}
                        <div className="view-contact-modal-card">
                            <div className="view-contact-modal-icon-wrap">
                                <FaUserTie />
                            </div>
                            <div className="view-contact-modal-info">
                                <label>Designation</label>
                                <span>{contact.designation || 'Not Specified'}</span>
                            </div>
                        </div>

                        {/* Mobile Number */}
                        <div className="view-contact-modal-card">
                            <div className="view-contact-modal-icon-wrap">
                                <FaPhoneAlt />
                            </div>
                            <div className="view-contact-modal-info">
                                <label>Mobile Number</label>
                                <a href={`tel:${contact.mobile || contact.phone}`} className="view-contact-modal-link">
                                    {contact.mobile || contact.phone}
                                </a>
                            </div>
                        </div>

                        {/* Email Address */}
                        <div className="view-contact-modal-card">
                            <div className="view-contact-modal-icon-wrap">
                                <FaEnvelope />
                            </div>
                            <div className="view-contact-modal-info">
                                <label>Email Address</label>
                                <a href={`mailto:${contact.email}`} className="view-contact-modal-link text-break">
                                    {contact.email}
                                </a>
                            </div>
                        </div>

                        {/* Submitted Date */}
                        <div className="view-contact-modal-card">
                            <div className="view-contact-modal-icon-wrap">
                                <FaCalendarAlt />
                            </div>
                            <div className="view-contact-modal-info">
                                <label>Submitted On</label>
                                <span>{formatDate(contact.createdAt)}</span>
                            </div>
                        </div>

                        {/* Resolved Date */}
                        <div className="view-contact-modal-card">
                            <div className="view-contact-modal-icon-wrap">
                                <BsCheck2All />
                            </div>
                            <div className="view-contact-modal-info">
                                <label>Resolved On</label>
                                <span>{contact.resolvedOn ? formatDate(contact.resolvedOn) : 'Pending Action'}</span>
                            </div>
                        </div>
                    </div>

                    {/* Message Box (Full Width Auto) */}
                    <div className="view-contact-modal-message-section">
                        <div className="view-contact-modal-message-header">
                            <FaCommentDots />
                            <span>Detailed Message / Project Requirements</span>
                        </div>
                        <div className="view-contact-modal-message-box">
                            {contact.message || contact.comment || 'No additional message provided.'}
                        </div>
                    </div>

                </div>

                {/* Modal Footer Actions */}
                <div className="view-contact-modal-footer">
                    <button type="button" className="view-contact-modal-btn dismiss" onClick={onClose}>
                        Close
                    </button>
                    {contact.status === 'In Progress' && (
                        <button 
                            type="button" 
                            className="view-contact-modal-btn resolve"
                            onClick={() => {
                                onClose();
                                onResolve(contact);
                            }}
                        >
                            <BsCheck2All /> Mark Resolved
                        </button>
                    )}
                </div>

            </div>
        </div>
    );
};

const PAGE_OPTIONS = [10, 20, 30, 40, 50, 100];

/* =========================================================
   MAIN VIEW CONTACT TABLE COMPONENT
========================================================= */
const ViewContact = () => {
    const [contacts, setContacts] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [expandedRow, setExpandedRow] = useState(null);
    const [viewingContact, setViewingContact] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const fetchContacts = async () => {
        try {
            const res = await axiosInstance.get('/api/contact/all');
            setContacts(Array.isArray(res.data) ? res.data : []);
        } catch (error) {
            if (error.response?.status !== 401) console.error(error);
        }
    };

    useEffect(() => { fetchContacts(); }, []);

    const toggleRow = (id) => setExpandedRow(prev => (prev === id ? null : id));
    const formatDate = (d) => d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '-';

    const filteredContacts = useMemo(() => {
        return contacts.filter(item => {
            if (statusFilter !== 'All' && item.status !== statusFilter) return false;
            if (searchQuery) {
                const q = searchQuery.toLowerCase();
                const mob = item.mobile || item.phone || '';
                const org = item.organisation || '';
                const des = item.designation || '';
                const srv = item.serviceRequired || '';
                return (
                    item.name?.toLowerCase().includes(q) || 
                    item.email?.toLowerCase().includes(q) || 
                    mob.includes(q) ||
                    org.toLowerCase().includes(q) ||
                    des.toLowerCase().includes(q) ||
                    srv.toLowerCase().includes(q)
                );
            }
            return true;
        });
    }, [contacts, searchQuery, statusFilter]);

    useEffect(() => { setCurrentPage(1); }, [searchQuery, statusFilter]);

    const paginatedContacts = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filteredContacts.slice(start, start + itemsPerPage);
    }, [filteredContacts, currentPage, itemsPerPage]);

    // Excel Export
    const handleExportExcel = async () => {
        if (filteredContacts.length === 0) return Swal.fire('Notice', 'No data to export.', 'info');

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Contact Submissions');

        worksheet.mergeCells('A1:I1');
        worksheet.getCell('A1').value = 'NUTRIVIO MEDICAL SERVICES PRIVATE LIMITED';
        worksheet.getCell('A1').font = { name: 'Arial', size: 15, bold: true, color: { argb: 'FF206E88' } };
        worksheet.getCell('A1').alignment = { vertical: 'middle', horizontal: 'center' };

        worksheet.mergeCells('A2:I2');
        worksheet.getCell('A2').value = `Institutional Contact Submissions Report (${statusFilter})`;
        worksheet.getCell('A2').font = { name: 'Arial', size: 11, bold: true, color: { argb: 'FF475569' } };
        worksheet.getCell('A2').alignment = { vertical: 'middle', horizontal: 'center' };

        worksheet.addRow([]);

        const headerRow = worksheet.addRow([
            'S.No', 'Name', 'Organisation', 'Designation', 'Mobile', 'Email Address', 'Service Required', 'Message', 'Status', 'Submitted On'
        ]);
        
        headerRow.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF50A5C0' } };
            cell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });

        filteredContacts.forEach((c, i) => {
            const row = worksheet.addRow([
                i + 1, 
                c.name, 
                c.organisation || 'N/A', 
                c.designation || 'N/A', 
                c.mobile || c.phone, 
                c.email, 
                c.serviceRequired || 'N/A', 
                c.message || c.comment, 
                c.status, 
                formatDate(c.createdAt)
            ]);
            
            row.eachCell((cell) => {
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
            });
            row.getCell(1).alignment = { horizontal: 'center' };
            
            const statusCell = row.getCell(9);
            statusCell.alignment = { horizontal: 'center' };
            statusCell.font = { bold: true, color: { argb: c.status === 'Resolved' ? 'FF16A34A' : c.status === 'In Progress' ? 'FF2563EB' : 'FFE67E22' } };
        });

        worksheet.columns = [
            { width: 8 }, { width: 22 }, { width: 25 }, { width: 20 }, { width: 18 }, { width: 28 }, { width: 30 }, { width: 45 }, { width: 15 }, { width: 22 }
        ];

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `Nutrivio_Contact_Submissions_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    // Actions Handler
    const handleAction = async (type, contact) => {
        if (type === 'view') {
            if (contact.status === 'Open') {
                try {
                    await axiosInstance.put(`/api/contact/update/${contact._id}`, { status: 'In Progress' });
                    fetchContacts(); 
                } catch(e) {}
            }
            setViewingContact(contact);
        }
        else if (type === 'resolve') {
            if (contact.status !== 'In Progress') {
                return Swal.fire('Notice', 'Please view and review the enquiry first before resolving it.', 'info');
            }
            const res = await Swal.fire({
                title: 'Mark as Resolved?',
                text: 'Move this institutional query to Resolved status.',
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#4A9731',
                confirmButtonText: 'Yes, Mark Resolved'
            });
            if (res.isConfirmed) {
                try {
                    await axiosInstance.put(`/api/contact/update/${contact._id}`, { status: 'Resolved' });
                    fetchContacts();
                    Swal.fire('Resolved', 'Status updated successfully.', 'success');
                } catch (error) { Swal.fire('Error', 'Status update failed.', 'error'); }
            }
        }
        else if (type === 'delete') {
            const res = await Swal.fire({
                title: 'Delete Entry?',
                text: 'This record will be permanently deleted.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#DC2626',
                confirmButtonText: 'Yes, Delete'
            });
            if (res.isConfirmed) {
                try {
                    await axiosInstance.delete(`/api/contact/delete/${contact._id}`);
                    fetchContacts();
                    Swal.fire('Deleted', 'Entry removed successfully.', 'success');
                } catch (error) { Swal.fire('Error', 'Deletion failed.', 'error'); }
            }
        }
    };

    return (
        <>
            <Helmet>
                <title>Contact Enquiries | Nutrivio Admin</title>
                <meta name="description" content="View and manage institutional contact submissions for Nutrivio Medical Services." />
            </Helmet>

            <div className="view-contact-container">
                
                {/* Modern Popup Modal */}
                <ContactViewModal 
                    contact={viewingContact} 
                    onClose={() => setViewingContact(null)} 
                    onResolve={(c) => handleAction('resolve', c)}
                    onDelete={(c) => handleAction('delete', c)}
                />
                
                <h2 className="view-contact-title">Institutional Contact Submissions</h2>

                {/* Top Controls */}
                <div className="view-contact-controls">
                    <div className="view-contact-search-wrapper">
                        <FaSearch className="view-contact-search-icon" />
                        <input 
                            type="text" 
                            className="view-contact-search-input" 
                            placeholder="Search Name, Organisation, Mobile, Email..." 
                            value={searchQuery} 
                            onChange={(e) => setSearchQuery(e.target.value)} 
                        />
                    </div>
                    
                    <div className="view-contact-action-group">
                        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="view-contact-limit-select">
                            <option value="All">All Status</option>
                            <option value="Open">Open</option>
                            <option value="In Progress">In Progress</option>
                            <option value="Resolved">Resolved</option>
                        </select>
                        <select className="view-contact-limit-select" value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                            {PAGE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt} / page</option>)}
                        </select>
                        <button className="view-contact-btn" onClick={handleExportExcel} title="Export to Excel">
                            <FaFileExport /> Export Excel
                        </button>
                    </div>
                </div>

                {/* Table Layout */}
                <div className="view-contact-table-wrapper">
                    <table className="view-contact-table">
                        <thead>
                            <tr>
                                <th className="col-sno">S.No</th>
                                <th className="col-name view-contact-align-left">Name</th>
                                <th className="col-contact hide-lt-768">Contact</th>
                                <th className="col-service hide-lt-991">Service Required</th>
                                <th className="col-date hide-lt-1200">Submitted On</th>
                                <th className="col-status hide-lt-768">Status</th>
                                <th className="col-action">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedContacts.length === 0 ? (
                                <tr><td colSpan="7" className="view-contact-no-data">No contact submissions found.</td></tr>
                            ) : (
                                paginatedContacts.map((c, i) => {
                                    const globalIndex = (currentPage - 1) * itemsPerPage + i;
                                    const isRowOpen = expandedRow === c._id;

                                    return (
                                    <React.Fragment key={c._id}>
                                        <tr className={`view-contact-main-row ${c.status === 'Open' ? 'unread' : ''} ${isRowOpen ? 'row-expanded' : ''}`}>
                                            
                                            {/* 1. S.No */}
                                            <td className="col-sno">{globalIndex + 1}</td>
                                            
                                            {/* 2. Name with Toggle Chevron (Works on ALL screens) */}
                                            <td className="col-name view-contact-align-left">
                                                <div className="view-contact-name-wrapper" onClick={() => toggleRow(c._id)}>
                                                    <span className="view-contact-name-strong">{c.name}</span>
                                                    <button type="button" className="view-contact-expand-btn" aria-label="Toggle Row Details">
                                                        {isRowOpen ? <FaChevronUp /> : <FaChevronDown />}
                                                    </button>
                                                </div>
                                            </td>

                                            {/* 3. Contact Mobile (> 768px) */}
                                            <td className="col-contact hide-lt-768">
                                                <strong className="view-contact-phone-text">{c.mobile || c.phone}</strong>
                                            </td>

                                            {/* 4. Service Required (> 991px) */}
                                            <td className="col-service hide-lt-991">
                                                <span className="view-contact-service-pill">{c.serviceRequired || 'General'}</span>
                                            </td>

                                            {/* 5. Submitted On (> 1200px) */}
                                            <td className="col-date hide-lt-1200">
                                                <span className="view-contact-date-text">{formatDate(c.createdAt)}</span>
                                            </td>

                                            {/* 6. Status (> 768px) */}
                                            <td className="col-status hide-lt-768">
                                                <span className={`view-contact-status-badge ${c.status.toLowerCase().replace(' ', '-')}`}>
                                                    {c.status}
                                                </span>
                                            </td>

                                            {/* 7. Actions (Always Visible) */}
                                            <td className="col-action">
                                                <div className="view-contact-action-btns">
                                                    <button className="view-contact-icon-btn view" title="View Full Details" onClick={() => handleAction('view', c)}>
                                                        <FaEye />
                                                    </button>
                                                    {c.status === 'In Progress' && (
                                                        <button className="view-contact-icon-btn resolve" title="Mark as Resolved" onClick={() => handleAction('resolve', c)}>
                                                            <BsCheck2All />
                                                        </button>
                                                    )}
                                                    {c.status === 'Resolved' && (
                                                        <button className="view-contact-icon-btn delete" title="Delete Record" onClick={() => handleAction('delete', c)}>
                                                            <FaTrashAlt />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>

                                        {/* =========================================================
                                            EXPANDED VIEW ROW (Organisation, Designation, Email, Message)
                                        ========================================================= */}
                                        {isRowOpen && (
                                            <tr className="view-contact-expanded-row">
                                                <td colSpan="7">
                                                    <div className="view-contact-expanded-content">
                                                        
                                                        {/* Status (Shows in expand ONLY on < 768px) */}
                                                        <div className="view-contact-exp-item show-exp-lt-768">
                                                            <span className="view-contact-exp-label">Status</span>
                                                            <div>
                                                                <span className={`view-contact-status-badge ${c.status.toLowerCase().replace(' ', '-')}`}>
                                                                    {c.status}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        {/* Contact Number (Shows in expand ONLY on < 768px) */}
                                                        <div className="view-contact-exp-item show-exp-lt-768">
                                                            <span className="view-contact-exp-label">Contact Number</span>
                                                            <span className="view-contact-exp-value">{c.mobile || c.phone}</span>
                                                        </div>

                                                        {/* Service Required (Shows in expand ONLY on < 991px) */}
                                                        <div className="view-contact-exp-item show-exp-lt-991">
                                                            <span className="view-contact-exp-label">Service Required</span>
                                                            <span className="view-contact-exp-value">{c.serviceRequired || 'N/A'}</span>
                                                        </div>

                                                        {/* Submitted Date (Shows in expand on <= 1200px) */}
                                                        <div className="view-contact-exp-item show-exp-lt-1200">
                                                            <span className="view-contact-exp-label">Submitted On</span>
                                                            <span className="view-contact-exp-value">{formatDate(c.createdAt)}</span>
                                                        </div>

                                                        {/* Organisation (In Expand on ALL screens) */}
                                                        <div className="view-contact-exp-item">
                                                            <span className="view-contact-exp-label">Organisation / Trust</span>
                                                            <span className="view-contact-exp-value">{c.organisation || 'Not Provided'}</span>
                                                        </div>

                                                        {/* Designation (In Expand on ALL screens) */}
                                                        <div className="view-contact-exp-item">
                                                            <span className="view-contact-exp-label">Designation</span>
                                                            <span className="view-contact-exp-value">{c.designation || 'Not Provided'}</span>
                                                        </div>

                                                        {/* Email Address (In Expand on ALL screens) */}
                                                        <div className="view-contact-exp-item">
                                                            <span className="view-contact-exp-label">Email Address</span>
                                                            <span className="view-contact-exp-value email-break">{c.email}</span>
                                                        </div>

                                                        {/* Message (Full Width Auto Box) */}
                                                        <div className="view-contact-exp-item view-contact-col-span-full">
                                                            <span className="view-contact-exp-label">Message / Project Requirements</span>
                                                            <div className="view-contact-text-content">
                                                                {c.message || c.comment || 'No specific requirements mentioned.'}
                                                            </div>
                                                        </div>

                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                )})
                            )}
                        </tbody>
                    </table>
                </div>

                {filteredContacts.length > 0 && (
                    <Pagination totalItems={filteredContacts.length} itemsPerPage={itemsPerPage} currentPage={currentPage} onPageChange={setCurrentPage} />
                )}
            </div>
        </>
    );
};

export default ViewContact;